import React from 'react';

import {
  IconSound,
  IconVector,
  IconImage,
  IconVideo,
  IconDownload,
  IconText,
  IconFont,
  IconUnknownFile,
  IconIllustrator,
  IconInDesign,
  IconPhotoshop,
  IconPremiere,
  IconSketch,
  IconArchive,
  IconZip,
  IconPresentation,
  IconSpreadsheet,
  IconExposure,
} from '@bynder/icons';

import styled from 'styled-components';
import { includes } from 'lodash';
import { Translate, useTranslate } from '@bynder/localization';
import {
  Card,
  Buffer,
  notify,
  token,
  Tooltip,
  Flex,
  Badge,
} from '@bynder/design-system';
import { API_ROUTES } from '../../../constants/app';
import {
  checkDownloadable,
  downloadAsset,
} from '../../../helpers/api/assetsApi';
import {
  AssetIsNotDownloadableNotification,
  DownloadingAssetInCollectionErrorNotification,
  DownloadingAssetInCollectionSuccessNotification,
} from '../../../types/notifications';
import { UserType, useUserType } from '../../../helpers/hooks';
import {
  CollectionLayoutOptions,
  CollectionMediaItem,
} from '../../../types/widgets/collections';
import { useGuideStore } from '../../../stores/guideStore';
import { useModalStore } from '../../../stores/modalStore';
import axios from 'axios';
import { waitForCollectionDownloadAvailable } from '../../../helpers/api/collectionsApi';
import { getTypographyWeight } from '../../shared/styled-components/constants';
import { addQuotes, toPx } from '../../guides/Inspector/helpers';

interface CollectionPreviewProps {
  preview: CollectionMediaItem;
  layout: CollectionLayoutOptions;
  isLast?: boolean;
  remainingAssets?: number;
  collectionId?: string;
  showAssetTitle?: boolean;
  showAssetType?: boolean;
  allowAssetRedirection?: boolean;
  showAssetDownload?: boolean;
  showOpenAssetInAssetBank: boolean;
  assetNameColor: string;
  onClick?: () => void;
  $borderRadius: string;
  $assetTitle?: {
    fontFamily: string;
    fontStyle: string;
    fontSize: string;
    textColor: string;
  };
}

const vectorTypes = ['svg', 'eps'];

const adobePhotoshopType = 'psd';
const adobeIllustratorType = 'ai';
const adobePhotoshopLargeDocumentType = 'psb';

const imageTypes = [
  'gif',
  'jpg',
  'jpeg',
  'png',
  'bmp',
  'tiff',
  'tif',
  'webp',
  adobePhotoshopLargeDocumentType,
  adobePhotoshopType,
  adobeIllustratorType,
  ...vectorTypes,
];

const audioTypes = [
  'acc',
  'ac3',
  'adspcm',
  'aif',
  'aiff',
  'amr',
  'flac',
  'pcm',
  'vorbis',
  'wma',
  'wav',
  'mp3',
];

const fontTypes = ['ttf', 'otf', 'woff', 'woff2'];

const videoTypes = [
  'mov',
  'avi',
  'flv',
  'f4v',
  'vob',
  'ts',
  'mts',
  'm2ts',
  'm2v',
  'm4v',
  'mkv',
  'mxf',
  '3g2',
  '3gp',
  '3gpp',
  'ogv',
  'webm',
  'mp4',
  'wmv',
  'mpg',
  'mpeg',
];

const rawTypes = [
  'raw',
  'raf',
  'dng',
  'arw',
  'rw2',
  'x3f',
  'cr2',
  'iiq',
  'rpef',
];

const compressedTypes = ['zip', 'rar'];

const executableTypes = ['exe', 'app', 'msi'];

const documentTypes = ['pages', 'rtf'];

const presentationTypes = ['ppt', 'pptx', 'pps', 'keynote'];

const sheetsTypes = ['xls', 'xlsx', 'numbers'];

const otherTypes = ['css', 'scss', 'html', 'eot', 'unknown'];

const textType = 'txt';

const adobeIndesignType = ['indd'];

const sketchType = 'sketch';

const adobePremiereType = 'prproj';

const pdfType = 'pdf';

//

export const CollectionCard: React.FC<CollectionPreviewProps> = ({
  preview,
  isLast = false,
  remainingAssets,
  showAssetTitle = true,
  showAssetType = true,
  allowAssetRedirection = true,
  showAssetDownload = true,
  collectionId,
  onClick,
  showOpenAssetInAssetBank,
  $assetTitle,
  $borderRadius,
  layout,
}) => {
  const { translate } = useTranslate();
  const { userType } = useUserType();
  const [isDownloading, setIsDownloading] = React.useState(false);
  const isEditMode = useGuideStore(s => s.isEditMode);
  const showAssetDetailOverlay = useModalStore.use.showAssetDetailOverlay();
  const [isAssetDownloadable, setIsAssetDownloadable] = React.useState(true);

  const getIsAssetDownloadable = async (): Promise<boolean> => {
    let assetIsDownloadable = true;
    if (userType === UserType.PUBLIC) {
      const { data: checkDownloadble } = await checkDownloadable(
        preview.assetId,
      );
      assetIsDownloadable = checkDownloadble.allowedMedia === preview.assetId;
    }
    return assetIsDownloadable;
  };

  const handleOpenPdf = async () => {
    setIsDownloading(true);
    const assetIsDownloadable = await getIsAssetDownloadable();
    try {
      if (assetIsDownloadable) {
        const { data } = await downloadAsset(preview.assetId, collectionId);
        if (data.download_id) {
          await waitForCollectionDownloadAvailable(data.download_id);
        }
        const response = await axios.get(data.signed_url, {
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: 'application/pdf' }),
        );
        window.open(url, '_blank');
      } else {
        setIsAssetDownloadable(false);
        notify(AssetIsNotDownloadableNotification);
      }
    } catch (error) {
      notify(DownloadingAssetInCollectionErrorNotification);
    }
    setIsDownloading(false);
  };

  const handleIndividualAssetDownload = async () => {
    setIsDownloading(true);
    const assetIsDownloadable = await getIsAssetDownloadable();
    try {
      if (assetIsDownloadable) {
        const { data } = await downloadAsset(preview.assetId, collectionId);
        if (data.download_id) {
          await waitForCollectionDownloadAvailable(data.download_id);
        }
        window.open(data.signed_url, '_parent');
        notify(DownloadingAssetInCollectionSuccessNotification);
      } else {
        setIsAssetDownloadable(false);
        notify(AssetIsNotDownloadableNotification);
      }
    } catch (error) {
      notify(DownloadingAssetInCollectionErrorNotification);
    }
    setIsDownloading(false);
  };

  const isAssetLimited =
    preview.isArchived || preview.isLimited || preview.isPrivate;

  const openAsset = () => {
    if (isEditMode) {
      return;
    }
    const isImage =
      includes(imageTypes, preview.assetExtension) && preview.assetImageUrl;

    if (allowAssetRedirection && userType !== UserType.PUBLIC) {
      if (isAssetLimited) {
        window.open(
          API_ROUTES.COLLECTION_ASSET_DETAIL(collectionId, preview.assetId),
          '_blank',
        );
      } else {
        window.open(API_ROUTES.ASSET_OPEN(preview.assetId), '_blank');
      }
    } else {
      if (isImage) {
        showAssetDetailOverlay(
          preview.assetName,
          preview.assetDetailUrl,
          preview.assetImageUrl,
          showOpenAssetInAssetBank,
        );
      } else if (preview.assetExtension === pdfType) {
        handleOpenPdf();
      } else {
        // Do nothing for non image, non pdf assets
        return;
      }
    }
  };

  const handleExpand = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    onClick();
  };

  if (isLast) {
    return (
      <Tooltip content={translate('WIDGETS_CONSTANTS.COLLECTION_EXPAND')}>
        <StyledCard $assetTitle={$assetTitle} $borderRadius={$borderRadius}>
          <Card onClick={handleExpand} variant="clean" as="div">
            <StyledCollectionCardLayover $borderRadius={$borderRadius}>
              + {remainingAssets}
            </StyledCollectionCardLayover>
            <Card.Frame
              height={
                layout === CollectionLayoutOptions.MASONRY ? 'fluid' : 'fixed'
              }
              isCheckeredBackground={preview.assetExtension === 'png'}
            >
              {preview.assetImageUrl && preview.assetImageUrl.length ? (
                <Card.Images>
                  <Card.Img
                    src={preview.assetImageUrl}
                    alt={preview.assetName}
                    contentResize="fit"
                  />
                </Card.Images>
              ) : (
                <Card.FilePlaceholder>
                  {getIcon(preview.assetExtension)}
                </Card.FilePlaceholder>
              )}
            </Card.Frame>
          </Card>
        </StyledCard>
      </Tooltip>
    );
  }

  return (
    <StyledCard $assetTitle={$assetTitle} $borderRadius={$borderRadius}>
      <Card
        onClick={isLast ? onClick : openAsset}
        variant="clean"
        contextAction={
          !isEditMode &&
          !isLast &&
          !isDownloading &&
          showAssetDownload && (
            <Card.ContextActionButton
              isDisabled={isAssetLimited || !isAssetDownloadable}
              tooltip={
                isAssetLimited
                  ? translate('WIDGETS_CONSTANTS.DOWNLOAD_ASSET_DISABLED')
                  : translate('WIDGETS_CONSTANTS.DOWNLOAD_ASSET')
              }
              aria-label="Menu"
              onClick={handleIndividualAssetDownload}
            >
              <IconDownload />
            </Card.ContextActionButton>
          )
        }
      >
        <Card.Frame
          icon={
            <Flex gap="3" direction="column">
              {preview.isArchived && showAssetType && (
                <Badge color="black-alpha">
                  <Translate id="WIDGETS_CONSTANTS.COLLECTION_ASSET_ARCHIVED" />
                </Badge>
              )}
              {preview.isLimited && showAssetType && (
                <Badge color="black-alpha">
                  <Translate id="WIDGETS_CONSTANTS.COLLECTION_ASSET_LIMITED" />
                </Badge>
              )}
            </Flex>
          }
          badge={showAssetType ? preview.assetExtension : null}
          height={
            layout === CollectionLayoutOptions.MASONRY ? 'fluid' : 'fixed'
          }
          isCheckeredBackground={preview.assetExtension === 'png'}
          progressBar={isDownloading && <Buffer noCornerRadius />}
        >
          {preview.assetImageUrl && preview.assetImageUrl.length ? (
            <Card.Images>
              <Card.Img
                src={preview.assetImageUrl}
                alt={preview.assetName}
                contentResize="fit"
              />
            </Card.Images>
          ) : (
            <Card.FilePlaceholder>
              {getIcon(preview.assetExtension)}
            </Card.FilePlaceholder>
          )}
        </Card.Frame>
        {showAssetTitle && <Card.Header title={preview.assetName} />}
      </Card>
    </StyledCard>
  );
};
const StyledCard = styled.div<{
  $borderRadius: string;
  $assetTitle?: {
    fontFamily: string;
    fontStyle: string;
    fontSize: string;
    textColor: string;
  };
}>`
  div[class*='Overlay-'],
  div[class*='FocusRing-'],
  div[class*='Children-'],
  div[class*='FrameStyled-'] {
    ${({ $borderRadius }) =>
      $borderRadius && `border-radius: ${toPx($borderRadius)}`};
  }
  div[class*='Title-'] {
    color: ${props =>
      props.$assetTitle?.textColor && props.$assetTitle?.textColor};
    font-family: ${props =>
      props.$assetTitle?.fontFamily &&
      addQuotes(props.$assetTitle?.fontFamily)};
    font-size: ${props =>
      props.$assetTitle?.fontSize && toPx(props.$assetTitle?.fontSize)};
    font-weight: ${props =>
      props.$assetTitle?.fontStyle &&
      getTypographyWeight(
        props.theme,
        props.$assetTitle?.fontFamily,
        props.$assetTitle?.fontStyle,
      )};
  }
`;

const StyledCollectionCardLayover = styled.div<{ $borderRadius: string }>`
  border-radius: ${props => toPx(props.$borderRadius)};
  z-index: 2;
  color: ${token.white};
  font-size: ${token.fontSize700};
  width: 100%;
  height: 100%;
  background-color: ${token.gray200a} !important;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
`;

const getIcon = (extension: string) => {
  switch (true) {
    // Image Files
    case imageTypes.includes(extension):
      return <IconImage />;
    case audioTypes.includes(extension):
      // Audio Files
      return <IconSound />;
    case videoTypes.includes(extension):
      // Video Files
      return <IconVideo />;

    // Font Types
    case fontTypes.includes(extension):
      return <IconFont />;

    // Raw Types
    case rawTypes.includes(extension):
      return <IconExposure />;

    // Compressed Files
    case compressedTypes.includes(extension):
      return <IconZip />;

    // Executable
    case executableTypes.includes(extension):
      return <IconArchive />;

    // Vector
    case vectorTypes.includes(extension):
      return <IconVector />;

    // Document Types
    case textType === extension:
      return <IconText />;
    case pdfType === extension:
      return (
        <StyledIconBackground backgroundColor="#BD0100">
          <IconText style={{ color: 'white' }} />
        </StyledIconBackground>
      );
    case documentTypes.includes(extension):
      return (
        <StyledIconBackground backgroundColor="#3E82F7">
          <IconText style={{ color: 'white' }} />
        </StyledIconBackground>
      );
    case presentationTypes.includes(extension):
      return (
        <StyledIconBackground backgroundColor="#FEA900">
          <IconPresentation style={{ color: 'white' }} />
        </StyledIconBackground>
      );
    // Specific Software
    case adobeIllustratorType === extension:
      return (
        <StyledIconBackground backgroundColor="#FF8000">
          <IconIllustrator style={{ color: 'black' }} />
        </StyledIconBackground>
      );
    case sketchType === extension:
      return <IconSketch style={{ color: '#FF9100' }} />;
    case adobePhotoshopType === extension:
      return (
        <StyledIconBackground backgroundColor="#45BEED">
          <IconPhotoshop style={{ color: 'black' }} />
        </StyledIconBackground>
      );
    case adobeIndesignType.includes(extension):
      return (
        <StyledIconBackground backgroundColor="#DD007B">
          <IconInDesign style={{ color: 'black' }} />
        </StyledIconBackground>
      );
    case adobePremiereType === extension:
      return (
        <StyledIconBackground backgroundColor="#E17EFF">
          <IconPremiere style={{ color: 'black' }} />
        </StyledIconBackground>
      );
    // Presentation
    case sheetsTypes.includes(extension):
      return (
        <StyledIconBackground backgroundColor="#009E55">
          <IconSpreadsheet style={{ color: 'white' }} />
        </StyledIconBackground>
      );
    // REst
    case otherTypes.includes(extension):
    default:
      return <IconUnknownFile />;
  }
};

const StyledIconBackground = styled.div<{ backgroundColor: string }>`
  padding: 2px;
  border-radius: ${token.radiusMedium};
  background-color: ${props => props.backgroundColor};
`;
